import React, {
  FunctionComponent,
  useReducer,
  useState,
  useEffect,
  Dispatch,
  SetStateAction
} from 'react'
import { FormattedMessage } from 'react-intl'
import { css } from '@emotion/css'
import { Row, Box } from '../../legacy-design-system/form'
import { CsHelpCircledIcon as HelpIcon } from '../../legacy-design-system/icons'
import { theme as deprecatedTheme } from '../../styled'
import {
  IconAdd,
  IconClose,
  theme
} from '@contrast-security-inc/design-system-foundations'
import { JenkinsThresholdInput } from '../../legacy-design-system/form-text'
import { WithTooltip } from '../../design-system'
import { SingleSelectComboboxObject } from '../../legacy-design-system/combobox'
import { ComboboxItem } from '../../legacy-design-system/combobox/shared'
import { getAssessRulesRoute } from '../route'

export interface Props {
  readonly orgUuid: string
  readonly dropdownItems: DropdownItemsGrouped[]
  readonly validateThreshold: (threshold: number) => boolean
  readonly allValues: DropdownItem[]
  readonly setFormHasChanged?: Dispatch<SetStateAction<boolean>>
  readonly addSeverityOrRuleToModel: (
    selectedRulesAndSeverities: DropdownItemsGrouped[]
  ) => void
}

export class DropdownItemsGrouped {
  public name: string
  public items: DropdownItem[]
}

export interface DropdownItem {
  readonly name: string
  readonly number?: number
  readonly group?: string
  readonly value?: string
  readonly index: number
}

export const addBlockTestId = 'add-block-test-id'
export const thresholdTestId = 'threshold-test-id'
export const closeIconTestId = 'close-icon-test-id'

export const dispatchRuleBlock = (
  values,
  {
    type,
    value,
    index,
    dropdownItemsWithRemoved
  }: {
    readonly type: 'add' | 'editRule' | 'editThreshold' | 'remove'
    readonly value: DropdownItem
    readonly index: number
    readonly dropdownItemsWithRemoved: DropdownItemsGrouped[]
  }
) => {
  switch (type) {
    case 'add':
      return [...values, value]
    case 'editRule':
      removeRuleOrSeverity(dropdownItemsWithRemoved, value)
      addRuleOrSeverity(dropdownItemsWithRemoved, values[index], value.index)
      return values.map((ruleBlock, mapIndex) => {
        if (mapIndex === index) {
          values[index] = value
          values[index].number = ruleBlock.number
          return value
        }
        return ruleBlock
      })
    case 'editThreshold':
      return values.map((ruleBlock, mapIndex) => {
        if (mapIndex === index) {
          ruleBlock.number = Number(value ? value : undefined)
        }
        return ruleBlock
      })
    case 'remove':
      addRuleOrSeverity(
        dropdownItemsWithRemoved,
        values[index],
        values[index].index
      )
      values = values.filter((_, filterIndex) => filterIndex !== index)

      return values
    default:
      return values
  }
}

function setAndValidateModel(
  addSeverityOrRuleToModel,
  validateThreshold,
  values
) {
  addSeverityOrRuleToModel(values)
  validateThreshold()
}

function HelpText({ children }) {
  return (
    <p
      className={css`
        max-width: 260px;
        overflow-wrap: normal;
        white-space: normal;
      `}
    >
      {children}
    </p>
  )
}

const closeIconCss = css`
  height: 13px;
  width: 13px;
  color: ${theme.color.neutral40};
  margin-left: 10px;
  &:hover {
    opacity: 1 !important;
  }
`

const RULES_LIMIT = 86

let removedItems: DropdownItem[] = []

const JenkinsRulesThresholdsSpan = ({
  message
}: {
  message: React.ReactNodeArray
}) => {
  return (
    <span
      className={css`
        padding-top: 20px;
        color: ${theme.color.neutral60};
        font-weight: ${deprecatedTheme.fontWeights.primary.bold};
      `}
    >{`${message} `}</span>
  )
}

export const JenkinsRulesThresholds: FunctionComponent<Props> = ({
  orgUuid,
  dropdownItems,
  allValues,
  setFormHasChanged,
  addSeverityOrRuleToModel,
  validateThreshold
}) => {
  const [values, dispatchRuleBlocks] = useReducer(dispatchRuleBlock, allValues)

  const [dropdownItemsWithRemoved, setDropdownItems] = useState(dropdownItems)

  const assessRulesLink = orgUuid
    ? getAssessRulesRoute(orgUuid)
    : 'https://docs.contrastsecurity.com/en/assess-rules.html'

  allValues.forEach((value) => {
    removeRuleOrSeverity(dropdownItems, value)
  })

  useEffect(() => {
    setDropdownItems(dropdownItems)
  }, [dropdownItems])

  useEffect(() => {
    setAndValidateModel(addSeverityOrRuleToModel, validateThreshold, values)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values])

  return (
    <div>
      <Row
        className={css`
          margin-bottom: -15px;
        `}
      >
        <Box basis={6}>
          <FormattedMessage
            id="title_integrationSettings_vulnerabilityRulesAndSeverities"
            children={(msg) => <JenkinsRulesThresholdsSpan message={msg} />}
          />

          <div
            className={css`
              display: inline-block;
              vertical-align: text-top;
              margin-top: -2px;
              margin-left: 5px;
            `}
          >
            <WithTooltip
              renderTooltipContent={() => (
                <div>
                  <FormattedMessage
                    id="detail_integrationsSettings_jenkinsVulnerabilitySeverity"
                    values={{
                      assessRulesLink: (
                        <div>
                          <br />
                          <a target="_blank" href={assessRulesLink}>
                            {
                              <FormattedMessage id="detail_integrationsSettings_jenkinsVulnerabilitySeverityLinkMessage" />
                            }
                          </a>
                        </div>
                      )
                    }}
                  />
                </div>
              )}
              placement="right"
            >
              {({ onMouseEnter, onMouseLeave }) => (
                <div>
                  <HelpIcon
                    color={theme.color.neutral50}
                    height={16}
                    width={16}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                  />
                </div>
              )}
            </WithTooltip>
          </div>
        </Box>
        <Box basis={6}>
          <FormattedMessage
            id="title_integrationsSettings_numberOfAllowedVulnerabilities"
            children={(msg) => <JenkinsRulesThresholdsSpan message={msg} />}
          />
          <div
            className={css`
              display: inline-block;
              vertical-align: text-top;
            `}
          >
            <WithTooltip
              renderTooltipContent={() => (
                <div>
                  <FormattedMessage
                    id="detail_integrationsSettings_numberOfAllowedVulnerabilities"
                    children={(msg) => <HelpText>{msg}</HelpText>}
                  />
                </div>
              )}
              placement="right"
            >
              {({ onMouseEnter, onMouseLeave }) => (
                <div
                  className={css`
                    margin-top: -2px;
                    margin-left: 5px;
                  `}
                >
                  <HelpIcon
                    color={theme.color.neutral50}
                    height={16}
                    width={16}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                  />
                </div>
              )}
            </WithTooltip>
          </div>
        </Box>
      </Row>
      <Row>
        <Box basis={12}>
          {values.map((value, index) => (
            <Row key={index}>
              <Box basis={6}>
                <SingleSelectComboboxObject
                  key={index}
                  items={
                    dropdownItemsWithRemoved as Array<
                      ComboboxItem<DropdownItem>
                    >
                  }
                  value={value}
                  onChange={(newRuleBlocksValue) => {
                    dispatchRuleBlocks({
                      type: 'editRule',
                      value: newRuleBlocksValue,
                      index,
                      dropdownItemsWithRemoved
                    })
                    if (setFormHasChanged) {
                      setFormHasChanged(true)
                    }
                  }}
                  itemToString={(item) => {
                    return item.name
                  }}
                />
              </Box>
              <Box basis={5}>
                <div
                  className={css`
                    display: flex;
                    align-items: center;
                  `}
                >
                  <JenkinsThresholdInput
                    data-testid={thresholdTestId}
                    key={index}
                    type={'text'}
                    value={value.number}
                    onChange={(newRuleBlocksValue) => {
                      dispatchRuleBlocks({
                        type: 'editThreshold',
                        value: newRuleBlocksValue,
                        index,
                        dropdownItemsWithRemoved
                      })
                      if (setFormHasChanged) {
                        setFormHasChanged(true)
                      }
                    }}
                    unitLabel={'title_integrationsSettings_vulnerabilities'}
                  />
                </div>
              </Box>
              <span
                className={css`
                  padding-left: 23px;
                  padding-top: 14px;
                `}
              >
                {values.length > 1 && (
                  <div data-testid={closeIconTestId}>
                    <IconClose
                      onClick={() => {
                        dispatchRuleBlocks({
                          type: 'remove',
                          value,
                          index,
                          dropdownItemsWithRemoved
                        })
                        if (setFormHasChanged) {
                          setFormHasChanged(true)
                        }
                      }}
                      size="sm"
                      className={closeIconCss}
                    />
                  </div>
                )}
              </span>
            </Row>
          ))}
          {values.length < RULES_LIMIT && (
            <div
              className={css`
                display: flex;
                align-items: center;
              `}
            >
              <IconAdd size="sm" />{' '}
              <FormattedMessage
                id="action_integrationsSettings_addRuleField"
                children={(msg) => (
                  <a
                    data-testid={addBlockTestId}
                    className={css`
                      cursor: pointer;
                      padding-bottom: 2px;
                      padding-left: 2px;
                    `}
                    onClick={() => {
                      dispatchRuleBlocks({
                        type: 'add',
                        value: { name: '', index: 0 },
                        index: 0,
                        dropdownItemsWithRemoved
                      })
                    }}
                  >
                    {msg}
                  </a>
                )}
              />
            </div>
          )}
        </Box>
      </Row>
      <div
        className={css`
          margin-top: 15px;
          margin-bottom: 10px;
        `}
      />
    </div>
  )
}

// Removes rule or severity back from list
export function removeRuleOrSeverity(
  dropdownItems: DropdownItemsGrouped[],
  value: DropdownItem
) {
  dropdownItems.forEach((severitiesAndRules) => {
    severitiesAndRules.items.forEach((severityOrRule, index) => {
      if (severityOrRule.name === value.name) {
        severitiesAndRules.items = [
          ...severitiesAndRules.items.slice(0, index),
          ...severitiesAndRules.items.slice(index + 1)
        ]
        removedItems.push(severityOrRule)
      }
    })
  })
}

// Adds rule or severity back to list
export function addRuleOrSeverity(
  dropdownItems: DropdownItemsGrouped[],
  valueToAdd: DropdownItem,
  index: number
) {
  let findRemoved

  removedItems = removedItems.filter((removedItem) => {
    if (removedItem.value === valueToAdd.value) {
      findRemoved = removedItem
    } else {
      return removedItem
    }
  })

  if (valueToAdd.name) {
    switch (valueToAdd.group) {
      case 'ALL':
        dropdownItems[0].items.splice(index, 0, findRemoved)
        dropdownItems[0].items.sort((a, b) => (a.index < b.index ? -1 : 1))
        break
      case 'SEVERITY':
        dropdownItems[1].items.splice(index, 0, findRemoved)
        dropdownItems[1].items.sort((a, b) => (a.index < b.index ? -1 : 1))
        break
      case 'ASSESS RULES':
        dropdownItems[2].items.splice(index, 0, findRemoved)
        dropdownItems[2].items.sort((a, b) => (a.index < b.index ? -1 : 1))
        break
    }
  }
}
